/**
 * The shared "type what this word means" card, front and back.
 *
 * Nouns, Verbs and Adjectives & adverbs all ask the same question about
 * different word classes, and they were three near-identical copies of the same
 * eighty lines. What actually differs is small and presentational: what sits
 * above the input, and one optional block on the back (a gender tint, a plural,
 * a conjugation table).
 *
 * So the interaction lives here once and each app supplies its own slots. That
 * the apps then *feel* identical to answer is the point, not a side effect —
 * they are the same task.
 *
 * Deliberately not generalised further: Der·Die·Das and Einbürgerungstest ask
 * genuinely different questions (pick an article, pick an option) and keep
 * their own cards. `adding-an-app.md` warns against abstracting on two
 * consumers; this one has four.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, MessageSquareQuote, TriangleAlert, X } from 'lucide-react';

import { EditCardDialog } from '@/components/practice/EditCardDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isCorrectAnswer } from '@/core/normalize';
import type { Card } from '@/core/types';
import { getLangFlag, LANG_NAMES } from '@/lib/lang-flags';
import { cn } from '@/lib/utils';

import type { AnswerEvent } from '@/apps/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyCard = Card<any>;

export function TypeTranslationCardFront({
  card,
  onAnswer,
  headword,
  subtitle,
  frameClassName,
}: {
  card: AnyCard;
  onAnswer: (event: AnswerEvent) => void;
  /** The word as the app wants it shown — nouns prefix a tinted article. */
  headword: ReactNode;
  /** Small line beneath it: the plural, the verb's markers, nothing. */
  subtitle?: ReactNode;
  /** Extra frame classes, e.g. the noun apps' gender-tinted left border. */
  frameClassName?: string;
}) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    startRef.current = Date.now();
    setValue('');
    inputRef.current?.focus();
  }, [card.id]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed) {
      inputRef.current?.focus();
      return;
    }
    onAnswer({
      correct: isCorrectAnswer(trimmed, Object.values(card.translations).flat()),
      user_answer: trimmed,
      time_ms: Date.now() - startRef.current,
    });
  };

  /**
   * "I don't know" — an honest miss rather than a guess.
   *
   * `user_answer: null` is the shared vocabulary for it: the results list
   * renders it as "skipped" instead of an empty "you wrote:", and the mistakes
   * list has nothing to diagnose from it, which is correct — there was no
   * misconception, just a blank.
   *
   * It lives here rather than in each app because Nouns, Verbs and Adjectives
   * already share this card, and the three of them were the only apps in the
   * tree without the button. Der·Die·Das, Einbürgerungstest and Vokabeln have
   * had it since they shipped; guessing on those apps was never necessary and
   * the typed decks were the odd ones out.
   */
  const skip = () => {
    onAnswer({
      correct: false,
      user_answer: null,
      time_ms: Date.now() - startRef.current,
    });
  };

  return (
    <div
      className={cn(
        'border-border bg-card flex flex-col items-center gap-8 rounded-2xl border p-8 shadow-sm sm:p-10',
        frameClassName,
      )}
    >
      <div className="text-center">
        <div className="font-display text-4xl font-semibold sm:text-5xl">{headword}</div>
        {subtitle ? <div className="text-muted-foreground mt-2 text-lg">{subtitle}</div> : null}
      </div>

      <Input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
        // Deliberately does not name the languages. It said "English or
        // Portuguese" while the app had one Brazilian user, which was correct
        // and invisible, and became wrong the moment a German joined and was
        // being asked for German or English. Deriving the pair from settings
        // would work, but it makes a presentational component fetch its own
        // data — and the answer check has always accepted any of the card's
        // languages, so naming them was never load-bearing. Dropping the claim
        // is simpler than making it configurable.
        placeholder="Type the translation…"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        className="max-w-md text-center text-lg"
      />

      <div className="flex w-full max-w-md flex-col gap-3">
        <Button size="lg" onClick={submit}>
          Check answer
        </Button>
        {/* Same position and weight as the other four apps, so muscle memory
            carries across. */}
        <button
          type="button"
          onClick={skip}
          className="text-muted-foreground mx-auto text-sm underline-offset-4 hover:underline"
        >
          I don&apos;t know
        </button>
      </div>
    </div>
  );
}

export function TranslationCardBack({
  card,
  correct,
  primary_lang,
  secondary_lang,
  isLastCard,
  onNext,
  headword,
  detail,
  nextLabel = 'Next card',
  frameClassName,
}: {
  card: AnyCard;
  correct: boolean;
  primary_lang: string;
  secondary_lang: string;
  isLastCard: boolean;
  onNext: () => void;
  /** The headline: "der Hund → die Hunde", "sich freuen", or just the word. */
  headword: ReactNode;
  /** Optional extra block, e.g. the verb conjugation table. */
  detail?: ReactNode;
  nextLabel?: string;
  frameClassName?: string;
}) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  // A correction is shown straight away rather than refetched: the session
  // already holds the card, and refetching mid-practice would stall the screen
  // for a change the user just made themselves.
  const [edited, setEdited] = useState<Partial<AnyCard> | null>(null);
  const shown = (edited ? { ...card, ...edited } : card) as AnyCard;

  return (
    <div
      className={cn(
        'border-border bg-card flex flex-col gap-5 rounded-2xl border p-8 shadow-sm sm:p-10',
        frameClassName,
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="font-display text-2xl font-semibold sm:text-3xl">{headword}</div>
        <div className="flex items-center gap-2">
          <EditCardDialog
            card={shown}
            primary_lang={primary_lang}
            secondary_lang={secondary_lang}
            onSaved={setEdited}
          />
          <div
            className={cn(
              'inline-flex items-center gap-1.5 font-medium',
              correct ? 'text-success' : 'text-destructive',
            )}
          >
            {correct ? <Check className="size-5" /> : <X className="size-5" />}
            <span>{correct ? 'Correct' : 'Incorrect'}</span>
          </div>
        </div>
      </div>

      <TranslationsList card={shown} primary={primary_lang} secondary={secondary_lang} />

      {detail}

      {shown.warning ? (
        <div className="bg-warning/10 flex items-start gap-2 rounded-md p-3 text-sm">
          <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
          <span>{shown.warning}</span>
        </div>
      ) : null}

      {shown.example ? (
        <div className="bg-muted/40 flex items-start gap-2 rounded-md p-3 text-sm">
          <MessageSquareQuote className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div>
            <div>{shown.example}</div>
            {shown.example_translations[secondary_lang] ? (
              <div className="text-muted-foreground mt-1 italic">
                ({shown.example_translations[secondary_lang]})
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <Button ref={buttonRef} size="lg" onClick={onNext} className="w-full">
        {isLastCard ? 'Finish session' : `${nextLabel} →`}
      </Button>
    </div>
  );
}

function TranslationsList({
  card,
  primary,
  secondary,
}: {
  card: AnyCard;
  primary: string;
  secondary: string;
}) {
  // Show the languages this card actually HAS, preferring the user's order.
  //
  // The two slots used to be hardcoded to the user's primary and secondary, so
  // a card carrying a language the user had not chosen simply did not display
  // it. Invisible while every deck was glossed in the same pair, and wrong the
  // moment a second target language arrived: the Portuguese nouns are glossed
  // in German and English, so a reader set to English-and-Portuguese saw the
  // English and an empty space where the German was.
  //
  // Preference is still honoured — a chosen language shown first, in order —
  // and the fallback only fills slots that would otherwise be blank.
  const available = Object.keys(card.translations).filter(
    (lang) => (card.translations[lang] ?? []).length > 0,
  );
  const chosen = [primary, secondary].filter((lang) => available.includes(lang));
  const rest = available.filter((lang) => !chosen.includes(lang));
  const [first, second] = [...chosen, ...rest];

  const PrimaryFlag = first ? getLangFlag(first) : null;
  const SecondaryFlag = second ? getLangFlag(second) : null;
  const primaryWords = first ? (card.translations[first] ?? []) : [];
  const secondaryWords = second ? (card.translations[second] ?? []) : [];

  return (
    <dl className="flex flex-col gap-2 text-base">
      {primaryWords.length > 0 && (
        <div className="flex items-baseline gap-3">
          {PrimaryFlag && <PrimaryFlag className="h-3.5 w-5 shrink-0" title={LANG_NAMES[first]} />}
          <dd>{primaryWords.join(', ')}</dd>
        </div>
      )}
      {secondaryWords.length > 0 && (
        <div className="flex items-baseline gap-3">
          {SecondaryFlag && (
            <SecondaryFlag className="h-3.5 w-5 shrink-0" title={LANG_NAMES[second]} />
          )}
          <dd>{secondaryWords.join(', ')}</dd>
        </div>
      )}
      {primaryWords.length === 0 && secondaryWords.length === 0 && (
        <div className="text-muted-foreground text-sm italic">No translations available.</div>
      )}
    </dl>
  );
}
